/**
 * Start screen, pause menu and death screen.
 *
 * Pointer events are the whole story here: `#ui-root` is click-through, so only
 * the panel of an *interactive* screen opts back in. The start screen deliberately
 * does not — its click has to reach the canvas underneath so the engine's own
 * listener can request pointer lock.
 *
 * Escape is not polled: the browser drops pointer lock itself, and we simply react
 * to `input:lock`. That keeps the menu correct when focus is lost to alt-tab too.
 */

import { isTouchDevice } from '../core/Engine.js';
import { LANGS, applyI18n, getLang, onLang, setLang } from './i18n.js';

const SHADOW_KEY = 'ob.shadows';
const QUALITY_KEY = 'ob.quality';

export const TUNING = {
  sensMin: 0.35,
  sensMax: 3.0,
  qualities: ['medium', 'high', 'ultra'],
};

export class Menu {
  constructor(ctx, root) {
    this.ctx = ctx;
    this.screen = 'none';
    this.gameState = 'menu';
    this.onVisibility = null;

    this.el = document.createElement('div');
    this.el.className = 'hud-menu';
    this.panel = document.createElement('div');
    this.panel.className = 'menu-panel';
    this.el.appendChild(this.panel);
    root.appendChild(this.el);

    this._baseSens = ctx.input?.sensitivity ?? 0.0022;
    this._build();
    this._bind();
    this.show('start');
  }

  /* -------------------------------- markup -------------------------------- */

  _build() {
    // --- start ------------------------------------------------------------
    this.startEl = frag(`
      <div class="menu-title">OPERATION BLACKOUT</div>
      <div class="menu-sub is-accent" data-i18n="${
        isTouchDevice() ? 'start.deploy' : 'start.deployKey'
      }"></div>
      <div class="menu-rule"></div>
      <!-- The keyboard/gamepad hint block is hidden on touch by CSS: there is no
           WASD on a phone, and a wall of bindings the player cannot use is worse
           than nothing on the smallest screen we support. -->
      <div class="menu-hint">WASD MOVE &nbsp;·&nbsp; SHIFT SPRINT &nbsp;·&nbsp; CTRL CROUCH &nbsp;·&nbsp; SPACE JUMP<br>
      LOOK: MOUSE <span class="menu-or">or</span> ARROW KEYS <span class="menu-or">or</span> RIGHT STICK<br>
      FIRE: LMB <span class="menu-or">or</span> ENTER <span class="menu-or">or</span> RT &nbsp;·&nbsp;
      ADS: RMB <span class="menu-or">or</span> RIGHT&nbsp;SHIFT <span class="menu-or">or</span> LT<br>
      R RELOAD &nbsp;·&nbsp; Q SWAP &nbsp;·&nbsp; ESC PAUSE &nbsp;·&nbsp; F3 STATS</div>
    `);

    // --- pause ------------------------------------------------------------
    this.pauseEl = frag(`
      <div class="menu-title" data-i18n="menu.paused"></div>
      <div class="menu-sub" data-i18n="menu.pausedSub"></div>
      <div class="menu-rule"></div>
      <button class="menu-btn" data-act="resume" data-i18n="menu.resume"></button>
      <div class="menu-row">
        <span data-i18n="menu.language"></span>
        <div class="menu-seg" data-ctl="lang"></div>
      </div>
      <div class="menu-row">
        <span data-i18n="menu.sensitivity"></span>
        <input type="range" min="35" max="300" step="1" data-ctl="sens">
        <output data-out="sens">1.00</output>
      </div>
      <div class="menu-row">
        <span data-i18n="menu.shadows"></span>
        <div class="menu-seg" data-ctl="shadows">
          <button data-shadows="off" data-i18n="menu.off"></button
          ><button data-shadows="on" data-i18n="menu.on"></button>
        </div>
      </div>
      <div class="menu-row">
        <span data-i18n="menu.quality"></span>
        <div class="menu-seg" data-ctl="quality"></div>
      </div>
      <div class="menu-row">
        <span data-i18n="menu.stats"></span>
        <div class="menu-seg" data-ctl="stats">
          <button data-stats="off" data-i18n="menu.off"></button
          ><button data-stats="on" data-i18n="menu.on"></button>
        </div>
      </div>
      <div class="menu-row menu-row-touch">
        <span data-i18n="menu.layout"></span>
        <div class="menu-seg">
          <button data-act="layout" data-i18n="menu.layoutEdit"></button>
        </div>
      </div>
      <div class="menu-rule" style="margin:18px 0 0"></div>
      <button class="menu-btn" data-act="restart" data-i18n="menu.restart"></button>
    `);

    // Language names are never translated — "日本語" is 日本語 in every locale, and a
    // player who has the UI in a language they cannot read needs to recognise their
    // own language's name to escape.
    const langSeg = this.pauseEl.querySelector('[data-ctl="lang"]');
    for (const [code, name] of Object.entries(LANGS)) {
      const b = document.createElement('button');
      b.textContent = name;
      b.dataset.lang = code;
      langSeg.appendChild(b);
    }

    const seg = this.pauseEl.querySelector('[data-ctl="quality"]');
    this.qualityBtns = TUNING.qualities.map((q) => {
      const b = document.createElement('button');
      b.textContent = q;
      b.dataset.q = q;
      seg.appendChild(b);
      return b;
    });

    this.sensInput = this.pauseEl.querySelector('[data-ctl="sens"]');
    this.sensOut = this.pauseEl.querySelector('[data-out="sens"]');
    this.sensInput.value = '100';

    // --- dead -------------------------------------------------------------
    this.deadEl = frag(`
      <div class="menu-title" data-i18n="menu.dead"></div>
      <div class="menu-sub" data-i18n="menu.deadSub"></div>
      <div class="menu-rule"></div>
      <button class="menu-btn" data-act="respawn" data-i18n="menu.respawn"></button>
      <button class="menu-btn" data-act="restart" data-i18n="menu.restart"></button>
    `);

    this._restoreSettings();
    // Paints the segment and tells the HUD where it stands, rather than leaving both
    // buttons unlit until the first click.
    this._setStats(false);

    // Both screens are detached fragments that survive across shows, so they are
    // translated once here and again whenever the language changes — they are not
    // rebuilt on open and would otherwise keep whatever language they were born in.
    this._applyLang();
    this._offLang = onLang(() => this._applyLang());
  }

  /**
   * Hand the screen over to the touch overlay so its buttons can be dragged.
   *
   * The panel goes away but the game stays paused, because the player needs to see
   * the buttons against the frame they will actually be used on — arranging them
   * over a dimmed menu would put them somewhere that looks right and plays wrong.
   */
  _editLayout() {
    const touch = this.ctx.touch;
    if (!touch) return;
    this.el.classList.remove('is-open');
    touch.onEditDone = () => {
      touch.endEdit();
      this.show('pause');
    };
    touch.beginEdit();
  }

  _applyLang() {
    applyI18n(this.startEl);
    applyI18n(this.pauseEl);
    applyI18n(this.deadEl);
    for (const b of this.pauseEl.querySelectorAll('[data-lang]')) {
      b.classList.toggle('is-on', b.dataset.lang === getLang());
    }
  }

  _bind() {
    const ctx = this.ctx;

    this.panel.addEventListener('click', (e) => {
      const btn = e.target.closest('button');
      if (!btn) return;
      if (btn.dataset.q) {
        this._setQuality(btn.dataset.q);
        return;
      }
      if (btn.dataset.stats) {
        this._setStats(btn.dataset.stats === 'on');
        return;
      }
      if (btn.dataset.lang) {
        setLang(btn.dataset.lang);
        return;
      }
      if (btn.dataset.shadows) {
        this._setShadows(btn.dataset.shadows === 'on');
        return;
      }
      switch (btn.dataset.act) {
        case 'resume':
          this.hide();
          ctx.input?.requestLock();
          break;
        case 'respawn':
          this._respawn();
          break;
        case 'restart':
          location.reload();
          break;
        case 'layout':
          this._editLayout();
          break;
      }
    });

    this.sensInput.addEventListener('input', () => {
      const mul = Number(this.sensInput.value) / 100;
      this.sensOut.textContent = mul.toFixed(2);
      if (ctx.input) ctx.input.sensitivity = this._baseSens * mul;
      ctx.events.emit('ui:sensitivity', { multiplier: mul });
    });

    ctx.events.on('game:state', (p) => {
      const s = p?.state;
      this.gameState = s ?? this.gameState;
      if (s === 'dead') this.show('dead');
      else if (s === 'playing' && this.screen === 'dead') this.hide();
    });

    ctx.events.on('input:lock', (p) => {
      if (p?.locked) {
        if (this.screen !== 'dead') this.hide();
      } else if (this.screen === 'none' && this.gameState === 'playing') {
        this.show('pause');
      }
    });
  }

  /* -------------------------------- actions ------------------------------- */

  _respawn() {
    const ctx = this.ctx;
    const spawn = ctx.level?.playerSpawn;
    if (ctx.player?.respawn && spawn) ctx.player.respawn(spawn.clone ? spawn.clone() : spawn);
    else if (ctx.player) ctx.player.dead = false;
    this.hide();
    ctx.events.emit('game:state', { state: 'playing' });
    ctx.input?.requestLock();
  }

  /**
   * Show or hide the frame-time overlay.
   *
   * It has always been on F3, which is exactly no use on the device where the number
   * matters most — a phone has no F3, and "what frame rate does this actually get on
   * your handset" is the one question about mobile that cannot be answered from a
   * desktop. So it gets a row in the pause menu, which is reachable by thumb.
   *
   * Goes through the event bus rather than reaching into `ctx.hud`: the overlay
   * belongs to the HUD, and this screen does not own it.
   */
  _setStats(on) {
    this._statsOn = !!on;
    for (const b of this.pauseEl.querySelectorAll('[data-stats]')) {
      b.classList.toggle('is-on', (b.dataset.stats === 'on') === this._statsOn);
    }
    this.ctx.events.emit('ui:stats', { on: this._statsOn });
  }

  /**
   * Apply a quality preset.
   *
   * Two ladders, because the two devices are limited by different things.
   *
   * On a desktop the only lever here is render resolution, and that is fine: a
   * desktop GPU eats this scene's geometry and the fragments are what is left.
   *
   * On a phone it is the wrong lever, and measurements from a real handset say so.
   * Three settings that differed *only* in pixel ratio gave 41 / 27 / 22 fps at
   * 1.0x / 1.5x / 2.0x, and fitting a line through them puts 14.2 ms of every frame
   * in a fixed cost that no resolution change can touch. 60 fps needs 16.7 ms total,
   * so resolution alone can never get there. That 14.2 ms is shadows: they are 57%
   * of the triangles and a third of the draw calls, because each cascade re-renders
   * every caster.
   *
   * So the phone ladder spends the *shadows* and holds the resolution near 1.0,
   * which is the opposite of the desktop one.
   *
   * It also stops the mobile ceiling from being thrown away. This function used to
   * recompute `maxPixelRatio` from the desktop formula unconditionally, so the
   * moment a phone player touched this control the tier's cap was replaced by
   * `min(dpr, 2)` — on a 3x screen, four times the fragments the tier had chosen.
   */
  /**
   * Apply saved graphics settings, or sensible defaults, once at startup.
   *
   * Running `_setQuality` here rather than only `_syncQuality` makes this ladder the
   * single authority on render resolution. `detectQuality()` picks 1.25 for a strong
   * phone, but the measured handset does 41 fps at 1.0 against roughly 33 at 1.25, so
   * the default preset is the one that maps to 1.0 — and, more importantly, the
   * highlighted button and the actual pixel ratio now always agree. They did not
   * before: the tier said 1.25 while the menu drew `medium`, which is that ladder's
   * name for 1.0.
   *
   * Shadows default on. They are expensive, but a first launch should show the game
   * as it is meant to look; now that the control says "shadows" rather than hiding
   * inside a quality preset, turning them off is a choice the player can find.
   */
  _restoreSettings() {
    let savedQ = null;
    let savedS = null;
    try {
      savedQ = localStorage.getItem(QUALITY_KEY);
      savedS = localStorage.getItem(SHADOW_KEY);
    } catch {
      /* private browsing */
    }
    const level = TUNING.qualities.includes(savedQ) ? savedQ : (this.ctx.quality?.level ?? 'high');
    this._setQuality(level);
    this._setShadows(savedS === null ? true : savedS === '1');
  }

  /**
   * Shadows, as their own control.
   *
   * This started life folded into the quality presets, where `medium` quietly meant
   * "no shadows". That was a bad trade and a player found it immediately by not
   * finding it: the label said QUALITY and nothing said SHADOWS, so the single
   * largest performance lever on a phone was hidden behind a word that does not
   * describe it. A control has to say what it does.
   *
   * Worth its own row on merit, too — it is not a shade of the same thing as render
   * resolution. Shadows are geometry cost (57% of every triangle, a third of the
   * draw calls, because each cascade re-renders every caster) and resolution is
   * fragment cost. They trade against completely different limits, so a player
   * chasing frames needs to be able to move them independently.
   */
  _setShadows(on) {
    this._shadowsOn = !!on;
    this.ctx.engine?.setShadowsEnabled?.(this._shadowsOn);
    try {
      localStorage.setItem(SHADOW_KEY, this._shadowsOn ? '1' : '0');
    } catch {
      /* private browsing: the setting still applies for this session */
    }
    this._syncShadows();
    this.ctx.events.emit('ui:shadows', { on: this._shadowsOn });
  }

  _syncShadows() {
    for (const b of this.pauseEl.querySelectorAll('[data-shadows]')) {
      b.classList.toggle('is-on', (b.dataset.shadows === 'on') === this._shadowsOn);
    }
  }

  /**
   * Render resolution, and only that now the shadows have moved out.
   *
   * The mobile ceiling is 1.25 and is never exceeded. This used to recompute
   * `maxPixelRatio` from the desktop formula unconditionally, so touching the control
   * on a phone replaced the tier's cap with `min(dpr, 2)` — four times the fragments
   * on a 3x screen.
   */
  _setQuality(level) {
    const ctx = this.ctx;
    if (!ctx.quality) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.quality.level = level;

    ctx.quality.maxPixelRatio = ctx.quality.mobile
      ? level === 'ultra'
        ? Math.min(dpr, 1.25)
        : level === 'high'
          ? Math.min(dpr, 1.15)
          : 1
      : level === 'ultra'
        ? Math.min(dpr, 2)
        : level === 'high'
          ? Math.min(dpr, 1.5)
          : 1;

    try {
      localStorage.setItem(QUALITY_KEY, level);
    } catch {
      /* see _setShadows */
    }
    ctx.engine?.resize?.();
    ctx.events.emit('ui:quality', { level });
    this._syncQuality();
  }

  _syncQuality() {
    const cur = this.ctx.quality?.level;
    for (const b of this.qualityBtns) b.classList.toggle('is-on', b.dataset.q === cur);
  }

  /* ------------------------------ visibility ------------------------------ */

  show(screen) {
    if (this.screen === screen) return;
    this.screen = screen;
    this.panel.replaceChildren(
      screen === 'start' ? this.startEl : screen === 'dead' ? this.deadEl : this.pauseEl
    );
    // The start screen must stay click-through so the canvas can grab the lock.
    this.el.classList.toggle('no-catch', screen === 'start');
    this.el.classList.toggle('is-dead', screen === 'dead');
    this.el.classList.add('is-open');
    this.onVisibility?.(true);
    // Announce it so the sim can actually stop; a pause menu that leaves the
    // world running is a lie.
    if (screen === 'pause' && this.gameState !== 'menu') {
      this.gameState = 'menu';
      this.ctx.events.emit('game:state', { state: 'menu' });
    }
  }

  hide() {
    if (this.screen === 'none') return;
    const was = this.screen;
    this.screen = 'none';
    this.el.classList.remove('is-open');
    this.onVisibility?.(false);
    if (was !== 'dead' && this.gameState !== 'playing') {
      this.gameState = 'playing';
      this.ctx.events.emit('game:state', { state: 'playing' });
    }
  }

  get open() {
    return this.screen !== 'none';
  }

  update() {
    // Escape while already unlocked never reaches pointerlockchange, so poll it.
    if (this.ctx.input?.actionPressed?.('pause')) {
      if (this.screen === 'pause') {
        this.hide();
        this.ctx.input.requestLock();
      } else if (this.screen === 'none') {
        this.show('pause');
      }
    }
  }
}

/** Returns a detached wrapper element — reusable across screen swaps, unlike a
 *  DocumentFragment, which is emptied the first time it is inserted. */
function frag(html) {
  const d = document.createElement('div');
  d.innerHTML = html.trim();
  return d;
}
